import React, { useState, useEffect, useContext } from 'react';
import DataTable from 'react-data-table-component';
import { useParams, Link } from 'react-router-dom'; // 🌟 Link を追加
import { AppSettings } from '../../config/app-settings.js';
import { apiFetch } from '../../utils/api';

import { IgrRangeSlider } from 'igniteui-react';
import 'igniteui-webcomponents/themes/light/bootstrap.css';

// 🎨 Color Admin スタイル
const customDataTableStyles = {
  table: { style: { backgroundColor: 'transparent' } },
  headRow: {
    style: {
      backgroundColor: 'transparent',
      borderBottom: '2px solid #dadfc1',
      minHeight: '45px',
    },
  },
  headCells: {
    style: {
      fontSize: '13px',
      fontWeight: 'bold',
      color: '#1f2d3d',
      paddingLeft: '16px',
      paddingRight: '16px',
    },
  },
  rows: {
    style: {
      fontSize: '13px',
      color: '#212529',
      minHeight: '70px',
      borderBottom: '1px solid #e3e6ec',
      backgroundColor: '#ffffff',
    },
    stripedStyle: {
      backgroundColor: '#f8f9fa',
      color: '#212529',
    },
    highlightOnHoverStyle: {
      backgroundColor: '#e9ecef !important',
      color: '#212529',
      transitionDuration: '0.1s',
      transitionProperty: 'background-color',
      borderBottomColor: '#e3e6ec',
      outline: 'none',
    },
  },
  pagination: {
    style: { borderTop: '1px solid #e3e6ec', fontSize: '13px', color: '#495057' },
  },
  cells: {
    style: { display: 'flex', alignItems: 'center' },
  },
};

// 🎨 カラム定義（画像：社内詳細リンク / タイトル：モール外部リンク）
const columns = [
  {
    name: '画像',
    width: '110px',
    cell: row => {
      const defaultImage = "/assets/img/brand/shop.svg";
      // 🌟 社内商品マスターUUIDを引き当て（マスターUUIDが優先）
      const productUuid = row.master_product_uuid || row.product_uuid || row.uuid;
      const imgElement = (
        <img
          src={row.image_url || defaultImage}
          alt={row.product_name || "商品イメージ"}
          style={{
            width: '80px',
            height: '80px',
            objectFit: 'cover',
            borderRadius: '4px',
            border: '1px solid #eee',
            transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
          }}
          className="img-thumbnail-hover"
          onError={(e) => { e.target.src = defaultImage; }}
        />
      );

      return productUuid ? (
        <Link to={`/purchase/products/edit/${productUuid}`} title="社内商品詳細画面を開く">
          {imgElement}
        </Link>
      ) : (
        <div title="詳細UUIDが見つかりません">{imgElement}</div>
      );
    }
  },
  {
    name: '順位',
    selector: row => row.rank,
    sortable: true,
    width: '90px',
    cell: row => (
      <div className="text-center w-100">
        <span className={`badge fs-14px ${row.rank <= 3 ? 'bg-warning text-dark' : 'bg-secondary'}`}>
          {row.rank}位
        </span>
      </div>
    ),
  },
  {
    name: '商品情報',
    selector: row => row.product_name,
    wrap: true,
    cell: row => (
      <div style={{ padding: '12px 0', width: '100%' }}>
        {/* 🌟 タイトルクリックは各モールの外部商品URLへ飛ぶリンクを維持 */}
        {row.product_url ? (
          <a
            href={row.product_url}
            target="_blank"
            rel="noreferrer"
            className="text-primary text-decoration-none fw-bold fs-13px lh-sm d-block mb-1"
            title="モールの公開ページを開く"
          >
            {row.product_name}
          </a>
        ) : (
          <span className="fw-bold fs-13px text-dark lh-sm d-block mb-1">{row.product_name}</span>
        )}
        <div className="fs-11px text-muted mt-1">
          <i className="fa fa-clock me-1"></i>
          取得日時: {row.checked_at ? row.checked_at.replace('T', ' ').substring(0, 16) : '-'}
        </div>
      </div>
    ),
  },
  {
    name: 'ジャンル名',
    width: '180px',
    wrap: true,
    cell: row => (
      <div className="fs-12px text-muted">
        {row.genre_name ? row.genre_name : '総合ランキング（ジャンルなし）'}
      </div>
    )
  },
  {
    name: 'ランキング種類',
    selector: row => row.ranking_type,
    width: '130px',
    cell: row => {
      const typeLabels = {
        'リアルタイム': { text: 'リアルタイム', color: 'bg-danger' },
        'デイリー': { text: 'デイリー', color: 'bg-info' }
      };
      const labelInfo = typeLabels[row.ranking_type] || { text: row.ranking_type, color: 'bg-dark' };

      return (
        <span className={`badge ${labelInfo.color} fs-11px`}>
          {labelInfo.text}
        </span>
      );
    }
  }
];

const MallRankingList = () => {
  const { mall } = useParams();
  const context = useContext(AppSettings);

  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStore, setSelectedStore] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [minRank, setMinRank] = useState('');
  const [maxRank, setMaxRank] = useState('');
  const [currentShops, setCurrentShops] = useState([]);

  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setSelectedStore('all');
    setSelectedType('all');
    setCurrentPage(1);

    if (context.userInfo && context.userInfo.malls) {
      const activeMall = context.userInfo.malls.find(m => m.path.endsWith(`/${mall}`));
      if (activeMall && activeMall.shops) {
        setCurrentShops(activeMall.shops);
      } else {
        setCurrentShops([]);
      }
    }
  }, [mall, context.userInfo]);

  useEffect(() => {
    const fetchRankings = async () => {
      setLoading(true);
      try {
        let url = `/rankings/${mall}?shop_uuid=${selectedStore}&ranking_type=${selectedType}&page=${currentPage}&per_page=${perPage}`;
        if (minRank) url += `&min_rank=${minRank}`;
        if (maxRank) url += `&max_rank=${maxRank}`;
        const response = await apiFetch(url);

        if (!response.ok) throw new Error('データの取得に失敗しました');

        const data = await response.json();
        setFilteredData(data.items);
        setTotalRows(data.total);
      } catch (error) {
        console.error("API Error:", error);
        setFilteredData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRankings();
  }, [mall, selectedStore, selectedType, minRank, maxRank, currentPage, perPage]);

  const handlePageChange = page => setCurrentPage(page);
  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  const displayMallName = {
    rakuten: '楽天市場',
    amazon: 'Amazon',
    yahoo: 'Yahoo!ショッピング'
  }[mall] || 'すべてのモール';

  return (
    <div>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><a href="#/">Home</a></li>
        <li className="breadcrumb-item active">ランキング管理</li>
      </ol>
      <h1 className="page-header">
        {displayMallName}のランキング <small>最新順</small>
      </h1>

      <div className="panel panel-inverse">
        <div className="panel-heading d-flex align-items-center">
          <h4 className="panel-title flex-grow-1">ランキング一覧</h4>

          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2">
              <div className="d-flex align-items-center bg-light rounded px-3 py-1 border" style={{ width: '350px' }}>
                <span className="fs-12px text-primary fw-bold bg-white px-2 py-1 rounded border text-nowrap me-3" style={{ minWidth: '95px', textAlign: 'center' }}>
                  {minRank || 1} 〜 {maxRank === 9999 ? '100' : (maxRank || 100)}位
                </span>

                <div className="flex-grow-1" style={{ paddingTop: '4px' }}>
                  <IgrRangeSlider
                    min={1}
                    max={100}
                    step={1}
                    lower={minRank || 1}
                    upper={maxRank === 9999 ? 100 : (maxRank || 100)}
                    onInput={(e) => {
                      const { lower, upper } = e.detail;
                      setMinRank(lower);
                      if (upper === 100) {
                        setMaxRank(9999);
                      } else {
                        setMaxRank(upper);
                      }
                    }}
                    onChange={() => setCurrentPage(1)}
                  />
                </div>
              </div>

              <select
                className="form-select form-select-sm"
                style={{ width: '150px' }}
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">すべての種類</option>
                <option value="realtime">リアルタイム</option>
                <option value="daily">デイリー</option>
              </select>

              <select
                className="form-select form-select-sm"
                style={{ width: '200px' }}
                value={selectedStore}
                onChange={(e) => {
                  setSelectedStore(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">全店舗</option>
                {currentShops.map((shop, index) => (
                  <option key={shop.uuid || shop.id || index} value={shop.uuid || shop.id}>
                    {shop.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="panel-body">
          <DataTable
            columns={columns}
            data={filteredData}
            progressPending={loading}
            progressComponent={
              <div className="text-center p-5">
                <div className="spinner-border text-primary mb-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="text-muted fs-12px">データを読み込み中...</div>
              </div>
            }
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            paginationPerPage={perPage}
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            highlightOnHover
            striped
            responsive
            customStyles={customDataTableStyles}
            noDataComponent={
              <div className="text-center p-5 text-muted">
                <i className="fa fa-trophy fa-2x mb-2 d-block"></i>
                表示するランキングデータがありません
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default MallRankingList;